import { defineStore } from "pinia";
import type { NavView } from "./router/navigationTypes";
import { sanitizeBetString } from "./utils";
import type { PostflopConvergenceReading } from "./api/postflop-convergence";
import type { MessageKey } from "./i18n";

export type { NavView } from "./router/navigationTypes";

export type SideView =
  | "ai-setup"
  | "preflop-action-tree"
  | "oop-range"
  | "ip-range"
  | "board"
  | "tree-config"
  | "run-solver"
  | "result";

export const saveConfigTmp = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks -- Pinia store accessor (defineStore), not a React hook
  const config = useConfigStore();
  // eslint-disable-next-line react-hooks/rules-of-hooks -- Pinia store accessor (defineStore), not a React hook
  const tmpConfig = useTmpConfigStore();

  tmpConfig.rangeRaw[0].set(config.rangeRaw[0]);
  tmpConfig.rangeRaw[1].set(config.rangeRaw[1]);

  tmpConfig.$patch({
    board: [...config.board],
    startingPot: config.startingPot,
    effectiveStack: config.effectiveStack,
    rakePercent: config.rakePercent,
    rakeCap: config.rakeCap,
    donkOption: config.donkOption,
    oopFlopBet: config.oopFlopBet,
    oopFlopRaise: config.oopFlopRaise,
    oopTurnBet: config.oopTurnBet,
    oopTurnRaise: config.oopTurnRaise,
    oopTurnDonk: config.oopTurnDonk,
    oopRiverBet: config.oopRiverBet,
    oopRiverRaise: config.oopRiverRaise,
    oopRiverDonk: config.oopRiverDonk,
    ipFlopBet: config.ipFlopBet,
    ipFlopRaise: config.ipFlopRaise,
    ipTurnBet: config.ipTurnBet,
    ipTurnRaise: config.ipTurnRaise,
    ipRiverBet: config.ipRiverBet,
    ipRiverRaise: config.ipRiverRaise,
    addAllInThreshold: config.addAllInThreshold,
    forceAllInThreshold: config.forceAllInThreshold,
    mergingThreshold: config.mergingThreshold,
    expectedBoardLength: config.expectedBoardLength,
    addedLines: config.addedLines,
    removedLines: config.removedLines,
  });
};

export const saveConfig = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks -- Pinia store accessor (defineStore), not a React hook
  const tmpConfig = useTmpConfigStore();
  // eslint-disable-next-line react-hooks/rules-of-hooks -- Pinia store accessor (defineStore), not a React hook
  const savedConfig = useSavedConfigStore();

  savedConfig.rangeRaw[0].set(tmpConfig.rangeRaw[0]);
  savedConfig.rangeRaw[1].set(tmpConfig.rangeRaw[1]);

  savedConfig.$patch({
    board: tmpConfig.board,
    startingPot: tmpConfig.startingPot,
    effectiveStack: tmpConfig.effectiveStack,
    rakePercent: tmpConfig.rakePercent,
    rakeCap: tmpConfig.rakeCap,
    donkOption: tmpConfig.donkOption,
    oopFlopBet: tmpConfig.oopFlopBet,
    oopFlopRaise: tmpConfig.oopFlopRaise,
    oopTurnBet: tmpConfig.oopTurnBet,
    oopTurnRaise: tmpConfig.oopTurnRaise,
    oopTurnDonk: tmpConfig.oopTurnDonk,
    oopRiverBet: tmpConfig.oopRiverBet,
    oopRiverRaise: tmpConfig.oopRiverRaise,
    oopRiverDonk: tmpConfig.oopRiverDonk,
    ipFlopBet: tmpConfig.ipFlopBet,
    ipFlopRaise: tmpConfig.ipFlopRaise,
    ipTurnBet: tmpConfig.ipTurnBet,
    ipTurnRaise: tmpConfig.ipTurnRaise,
    ipRiverBet: tmpConfig.ipRiverBet,
    ipRiverRaise: tmpConfig.ipRiverRaise,
    addAllInThreshold: tmpConfig.addAllInThreshold,
    forceAllInThreshold: tmpConfig.forceAllInThreshold,
    mergingThreshold: tmpConfig.mergingThreshold,
    expectedBoardLength: tmpConfig.expectedBoardLength,
    addedLines: tmpConfig.addedLines,
    removedLines: tmpConfig.removedLines,
  });
};

/** 결과 화면이 띄우는 postflop solve 품질 관측값. */
export type PostflopConvergenceNotice = PostflopConvergenceReading;

export interface PreviewConvergence {
  readonly currentIteration: number;
  readonly currentExploitability: number;
  readonly confidence: number;
  readonly isConverged: boolean;
  readonly convergenceWarning: string;
  readonly strategyStability: number;
  readonly historyPathDelta: number;
}

export const useStore = defineStore("app", {
  state: () => ({
    navView: "home" as NavView,
    sideView: "ai-setup" as SideView,
    headers: {
      "ai-setup": ["solverStage.aiSetup"],
      "preflop-action-tree": ["solverStage.preflopActionTree"],
      "oop-range": ["solverStage.oopRange"],
      "ip-range": ["solverStage.ipRange"],
      board: ["solverStage.board"],
      "tree-config": ["solverStage.treeConfiguration"],
      "run-solver": ["solverStage.runSolver"],
      result: ["solverStage.result"],
    } as Record<SideView, MessageKey[]>,
    isSolverRunning: false,
    isFinalizing: false,
    isSolverPaused: false,
    isSolverFinished: false,
    isPreviewMode: false,
    isTreeViewMode: false,
    previewConvergence: null as PreviewConvergence | null,
    configSource: "manual" as "manual" | "ai-setup",
    jobId: null as string | null,
    configHash: null as string | null,
    /**
     * 지금 화면에 실린 결과의 수렴 관측값. 결과를 싣는 같은 트랜잭션에서 함께 바뀌므로
     * 다른 결과로 넘어가면 이전 실행의 경고가 남지 않는다.
     *
     * 출처는 저장 tree 메타데이터(`/api/cache/:hash/status`)라 handoff·공유 링크·history·
     * refresh 어느 진입이든 같은 근거를 얻는다.
     */
    postflopConvergenceNotice: null as PostflopConvergenceNotice | null,
  }),

  getters: {
    hasSolverRun: (state) => {
      return (
        state.isSolverRunning ||
        state.isFinalizing ||
        state.isSolverPaused ||
        state.isSolverFinished ||
        state.isPreviewMode
      );
    },

    // 결과 뷰어가 보여줄 내용이 있는지. hasSolverRun 과 다르다 — 트리 뷰는
    // 솔브 없이도 보여줄 것이 있고, 솔브 진행 중에는 아직 보여줄 결과가 없다.
    hasResultToDisplay: (state) => {
      return (
        state.isSolverFinished || state.isPreviewMode || state.isTreeViewMode
      );
    },
  },
});

export const useConfigStore = defineStore("config", {
  state: () => ({
    range: [
      Array.from({ length: 13 * 13 }, () => 0),
      Array.from({ length: 13 * 13 }, () => 0),
    ],
    rangeRaw: [
      Float32Array.from({ length: (52 * 51) / 2 }, () => 0),
      Float32Array.from({ length: (52 * 51) / 2 }, () => 0),
    ] as Float32Array[],
    rangeVersion: 0, // range 변경 시 증가시켜 컴포넌트 강제 업데이트
    board: [] as number[],
    startingPot: 20,
    effectiveStack: 100,
    rakePercent: 0,
    rakeCap: 0,
    donkOption: false,
    oopFlopBet: "25,33,50,100",
    oopFlopRaise: "50,100",
    oopTurnBet: "25,33,50,100",
    oopTurnRaise: "50,100",
    oopTurnDonk: "",
    oopRiverBet: "25,33,50,100",
    oopRiverRaise: "50,100",
    oopRiverDonk: "",
    ipFlopBet: "25,33,50,100",
    ipFlopRaise: "50,100",
    ipTurnBet: "25,33,50,100",
    ipTurnRaise: "50,100",
    ipRiverBet: "25,33,50,100",
    ipRiverRaise: "50,100",
    addAllInThreshold: 150,
    forceAllInThreshold: 20,
    mergingThreshold: 10,
    expectedBoardLength: 0,
    addedLines: "",
    removedLines: "",
  }),

  getters: {
    oopFlopBetSanitized: (s) => sanitizeBetString(s.oopFlopBet, false),
    oopFlopRaiseSanitized: (s) => sanitizeBetString(s.oopFlopRaise, true),
    oopTurnBetSanitized: (s) => sanitizeBetString(s.oopTurnBet, false),
    oopTurnRaiseSanitized: (s) => sanitizeBetString(s.oopTurnRaise, true),
    oopTurnDonkSanitized: (s) => sanitizeBetString(s.oopTurnDonk, false),
    oopRiverBetSanitized: (s) => sanitizeBetString(s.oopRiverBet, false),
    oopRiverRaiseSanitized: (s) => sanitizeBetString(s.oopRiverRaise, true),
    oopRiverDonkSanitized: (s) => sanitizeBetString(s.oopRiverDonk, false),
    ipFlopBetSanitized: (s) => sanitizeBetString(s.ipFlopBet, false),
    ipFlopRaiseSanitized: (s) => sanitizeBetString(s.ipFlopRaise, true),
    ipTurnBetSanitized: (s) => sanitizeBetString(s.ipTurnBet, false),
    ipTurnRaiseSanitized: (s) => sanitizeBetString(s.ipTurnRaise, true),
    ipRiverBetSanitized: (s) => sanitizeBetString(s.ipRiverBet, false),
    ipRiverRaiseSanitized: (s) => sanitizeBetString(s.ipRiverRaise, true),
  },
});

export const useTmpConfigStore = defineStore("tmpConfig", {
  state: () => ({
    rangeRaw: [
      Float32Array.from({ length: (52 * 51) / 2 }, () => 0),
      Float32Array.from({ length: (52 * 51) / 2 }, () => 0),
    ] as Float32Array[],
    board: [] as number[],
    startingPot: 0,
    effectiveStack: 0,
    rakePercent: 0,
    rakeCap: 0,
    donkOption: false,
    oopFlopBet: "",
    oopFlopRaise: "",
    oopTurnBet: "",
    oopTurnRaise: "",
    oopTurnDonk: "",
    oopRiverBet: "",
    oopRiverRaise: "",
    oopRiverDonk: "",
    ipFlopBet: "",
    ipFlopRaise: "",
    ipTurnBet: "",
    ipTurnRaise: "",
    ipRiverBet: "",
    ipRiverRaise: "",
    addAllInThreshold: 0,
    forceAllInThreshold: 0,
    mergingThreshold: 0,
    expectedBoardLength: 0,
    addedLines: "",
    removedLines: "",
  }),
});

export const useSavedConfigStore = defineStore("savedConfig", {
  state: () => ({
    rangeRaw: [
      Float32Array.from({ length: (52 * 51) / 2 }, () => 0),
      Float32Array.from({ length: (52 * 51) / 2 }, () => 0),
    ] as Float32Array[],
    board: [] as number[],
    startingPot: 0,
    effectiveStack: 0,
    rakePercent: 0,
    rakeCap: 0,
    donkOption: false,
    oopFlopBet: "",
    oopFlopRaise: "",
    oopTurnBet: "",
    oopTurnRaise: "",
    oopTurnDonk: "",
    oopRiverBet: "",
    oopRiverRaise: "",
    oopRiverDonk: "",
    ipFlopBet: "",
    ipFlopRaise: "",
    ipTurnBet: "",
    ipTurnRaise: "",
    ipRiverBet: "",
    ipRiverRaise: "",
    addAllInThreshold: 0,
    forceAllInThreshold: 0,
    mergingThreshold: 0,
    expectedBoardLength: 0,
    addedLines: "",
    removedLines: "",
  }),
});
